"use client"
import { h4Style, h5Style, paragraphStyle } from "@/data/styles";
import { Developer } from "@/data/types";
import clsx from "clsx";
import Image from "next/image";
import placeholder from '@/assets/images/developer-placeholder.svg'
import { ArrowOutwardIcon } from "@/components/icons";

interface Props {
  data: Developer[]
}

interface DeveloperComponentProps {
  data: Developer
  idx: number
}

export default function Developers({ data }: Props) {
  const isEmpty = !data || data.length === 0;

  if (isEmpty) {
    return (
      <div className="max-w-[1440px] mx-auto px-[20px] md:px-[50px] py-[60px] flex flex-col items-center text-center">
        <p className={clsx(h4Style, "text-gray-600")}>
          No developers available
        </p>
        <p className={clsx(h5Style, "text-gray-500")}>
          Once developers are added, they’ll appear here automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[50px] py-[60px] grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-[18px] gap-y-[30px]">
      {data.map((developer, idx) => (
        <DeveloperComponent key={idx} idx={idx} data={developer} />
      ))}
    </div>
  );
}


const DeveloperComponent = ({ data }: DeveloperComponentProps) => {
  const { name, description, logo_url, image_url } = data
  return (
    <div className="flex flex-col gap-[10px] text-[#302E2D] w-full group">
      <div className="relative w-full aspect-square rounded-[4px] flex justify-center items-center overflow-hidden bg-[#EBE1D4]">
        {logo_url && <Image
          src={logo_url}
          alt={name ?? "Developer image"}
          width={150}
          height={150}
          className="w-[120px] lg:w-[150px] h-auto relative z-10 transition-opacity duration-300 group-hover:opacity-0"
        />}
        <Image 
          src={image_url || placeholder} alt={name ?? "Developer image"} fill 
          className="absolute inset-0 object-cover opacity-0 scale-110 translate-y-2 transition-all 
          duration-500 ease-out group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0"
        />

        <ArrowOutwardIcon
          width={17} height={17}
          className="absolute bottom-4 right-4 p-[15px] flex items-center justify-center 
          rounded-full bg-white/70 backdrop-blur-md shadow-[0_4px_14px_rgba(0,0,0,0.08)] 
          opacity-0 scale-90 translate-y-1 transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] 
          group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0 hover:shadow-[0_6px_18px_rgba(0,0,0,0.12)]"
        />
      </div>

      <h4 title={name} className={clsx(h4Style, 'truncate')}>{name}</h4>
      <p className={clsx(paragraphStyle, "line-clamp-3")}>{description}</p>
    </div>
  )
}


